import { Module } from 'vuex';

import { taxAccountingEntriesService } from '@/services/tax-accounting-entries/tax-accounting-entries.service';
import {
  TaxAccountingEntry,
  TaxAccountingEntryPayload
} from '@/services/tax-accounting-entries/tax-accounting-entries.types';
import { RootState } from '@/store';

export interface TaxAccountingEntryState {
  taxAccountingEntry: TaxAccountingEntry;
  taxAccountingEntryLoading: boolean;
}

const state: TaxAccountingEntryState = {
  taxAccountingEntry: null,
  taxAccountingEntryLoading: false
};

const options: Module<TaxAccountingEntryState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setTaxAccountingEntry: (
      { commit, dispatch },
      taxAccountingEntry: TaxAccountingEntry | string | number
    ): Promise<void> => {
      if (typeof taxAccountingEntry === 'number' || typeof taxAccountingEntry === 'string') {
        return dispatch('getTaxAccountingEntryById', taxAccountingEntry);
      }
      commit('taxAccountingEntry', taxAccountingEntry);
      if (taxAccountingEntry && (taxAccountingEntry as TaxAccountingEntry).id) {
        return dispatch('getTaxAccountingEntryById', (taxAccountingEntry as TaxAccountingEntry).id);
      }
    },
    getTaxAccountingEntryById: ({ commit, dispatch }, id: number): Promise<void> => {
      commit('taxAccountingEntryLoading', true);
      return taxAccountingEntriesService
        .get(id)
        .then(entry => entry && commit('taxAccountingEntry', entry))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('taxAccountingEntryLoading', false));
    },
    newTaxAccountingEntry: ({ commit, dispatch }): Promise<void> => {
      commit('taxAccountingEntryLoading', true);
      return taxAccountingEntriesService
        .newEntry()
        .then(entry => entry && commit('taxAccountingEntry', entry))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('taxAccountingEntryLoading', false));
    },
    createTaxAccountingEntry: ({ commit, dispatch }, payload: TaxAccountingEntryPayload): Promise<void> => {
      commit('taxAccountingEntryLoading', true);
      return taxAccountingEntriesService
        .create(payload)
        .then(entry => entry && commit('taxAccountingEntry', entry))
        .then(() => dispatch('alert/pushSuccess', 'Compte de taxe ajouté !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('taxAccountingEntryLoading', false));
    },
    updateTaxAccountingEntry: (
      { commit, dispatch, state },
      payload: Partial<TaxAccountingEntryPayload>
    ): Promise<void> => {
      if (!state.taxAccountingEntry?.id) return Promise.resolve();
      commit('taxAccountingEntryLoading', true);
      return taxAccountingEntriesService
        .update(state.taxAccountingEntry.id, payload)
        .then(entry => entry && commit('taxAccountingEntry', entry))
        .then(() => dispatch('alert/pushSuccess', 'Compte de taxe mis à jour !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('taxAccountingEntryLoading', false));
    },
    deleteTaxAccountingEntry: ({ state, dispatch }): Promise<void> => {
      if (!state.taxAccountingEntry?.id) return Promise.resolve();
      return taxAccountingEntriesService
        .destroy(state.taxAccountingEntry.id)
        .then(() => dispatch('alert/pushSuccess', 'Compte de taxe supprimé !', { root: true }))
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          throw error;
        });
    },
    clearTaxAccountingEntry: ({ dispatch }): Promise<void> => {
      return dispatch('setTaxAccountingEntry', null);
    }
  },
  mutations: {
    taxAccountingEntry: (state, entry: TaxAccountingEntry) => (state.taxAccountingEntry = entry),
    taxAccountingEntryLoading: (state, loading: boolean) => (state.taxAccountingEntryLoading = loading)
  }
};

export default options;
